<template>
  <WdsPageSignup class="wds-page-signup-login">
    <template #left>
      <LeftTeaser :title="title" :subtitles="subtitles" :benefits="benefits" />
    </template>

    <template #nav-right>
      <AuthLanguageSwitcher />
    </template>

    <template #right>
      <WdsHeading as="h1" type="title1">
        {{ $t('Select your profile') }}
      </WdsHeading>

      <WdsStack class="!tw-mt-7">
        <div
          class="account-type-card"
          data-test="profile-company-select"
          @click="onSelectAccountType('company')"
        >
          <WdsStack align-top class="!tw-mb-4" justify-space-between>
            <WdsIcon name="building" :size="32" />
          </WdsStack>
          <WdsText large>
            {{ $t("I'm a client") }}
          </WdsText>
          <WdsText large secondary>
            {{ $t('Find, hire, and manage talent.') }}
          </WdsText>
        </div>

        <div
          class="account-type-card"
          data-test="profile-talent-select"
          @click="onSelectAccountType('marketplace')"
        >
          <WdsStack align-top class="!tw-mb-4" justify-space-between>
            <WdsIcon name="user" :size="32" />
          </WdsStack>
          <WdsText large>
            {{ $t("I'm a freelancer") }}
          </WdsText>
          <WdsText large secondary>
            {{ $t('Find jobs and get paid faster.') }}
          </WdsText>
        </div>
      </WdsStack>

      <WdsDivider class="!tw-mt-7 !tw-mb-7 tw-text-gray-600 tw-text-base">
        {{ $t('Or') }}
      </WdsDivider>

      <div
        class="partner-card"
        data-test="profile-partner-select"
        @click="onSelectAccountType('partner')"
      >
        <WdsStack align-top class="!tw-mb-4" justify-space-between>
          <WdsIcon name="handshake" :size="32" />
        </WdsStack>
        <WdsText large>
          {{ $t("I'm a partner") }}
        </WdsText>
        <WdsText large secondary>
          {{ $t('Collaborate and grow with our platform.') }}
        </WdsText>
      </div>

      <WdsDivider class="!tw-mt-7 !tw-mb-7 tw-text-gray-600 tw-text-base">
        {{ $t('Or') }}
      </WdsDivider>

      <div class="recruiter-card" @click="recruiterOpen = !recruiterOpen">
        <WdsStack align-start justify-space-between>
          <div class="click-to-expand">
            <WdsText large>
              {{ $t("I'm a recruiter") }}
            </WdsText>
            <WdsText large secondary>
              {{ $t('Add, manage, and submit candidates.') }}
            </WdsText>
          </div>

          <div class="plus" :class="{ 'animate-plus': recruiterOpen }">
            <WdsIcon name="close" :size="24" />
          </div>
        </WdsStack>
        <Transition name="recruiter-animation" @enter="handler">
          <div v-if="recruiterOpen" class="!tw-mt-4" :style="{ maxHeight: `${maxHeight}px` }">
            <WdsText secondary>
              {{ $t('recruiters.choose-account-type') }}
            </WdsText>

            <a
              href="https://help.worksome.dk/en/collections/2512362-for-recruiters"
              target="_blank"
              class="read-more hover:tw-underline"
            >
              {{ $t('Using Worksome as a recruiter') }}
            </a>
          </div>
        </Transition>
      </div>

      <WdsText class="!tw-mt-7">
        {{ $t('Already have an account?') }}
        <a href="/login" class="hover:tw-underline">{{ $t('Log in') }}</a>
      </WdsText>
    </template>
  </WdsPageSignup>
</template>

<script>
import {
  WdsDivider,
  WdsHeading,
  WdsIcon,
  WdsPageSignup,
  WdsStack,
  WdsText,
} from '@worksome/wds-components'
import AuthLanguageSwitcher from '@/components/auth/AuthLanguageSwitcher.vue'
import LeftTeaser from '@/components/auth/left/LeftTeaser.vue'
import { $t } from '@/composables/translation'

export default {
  name: 'ChooseAccountTypePage',
  components: {
    AuthLanguageSwitcher,
    LeftTeaser,
    WdsDivider,
    WdsHeading,
    WdsIcon,
    WdsPageSignup,
    WdsStack,
    WdsText,
  },
  setup() {
    return {
      title: $t('Welcome to Worksome!'),
      subtitles: [
        $t(
          'Worksome helps clients to easily find, hire and manage highly skilled freelancers on flexible terms.',
        ),
      ],
      benefits: [
        $t('Find high quality work or the talents you need'),
        $t('Compliance guaranteed for each hire'),
        $t('Automated contracts, billing, and payments'),
      ],
    }
  },
  data() {
    return {
      recruiterOpen: false,
      maxHeight: 1000,
    }
  },
  methods: {
    onSelectAccountType(accountType) {
      let url
      if (accountType === 'marketplace') {
        url = '/register/marketplace'
      } else if (accountType === 'partner') {
        url = '/register/partner'
      } else {
        url = '/register/company'
      }
      window.location.href = url
    },
    handler(el) {
      this.maxHeight = el.scrollHeight + 50
    },
  },
}
</script>

<style lang="scss" scoped>
a {
  @apply tw-font-medium;
}

.wds-divider-container {
  @apply tw-text-gray-600;
}

.account-type-card {
  @apply tw-flex-grow;
  flex-basis: 0;
  border: 1px solid #dfdfe6;
  border-radius: 6px;
  @apply tw-p-4;

  &:nth-child(1) {
    @apply tw-mr-4;
  }

  &:nth-child(2) {
    @apply tw-ml-4;
  }

  & svg {
    @apply tw-fill-gray-900;
  }

  &:hover {
    @apply tw-bg-true-gray-100;
    @apply tw-cursor-pointer;

    & svg {
      @apply tw-fill-gray-900;
    }
  }
}

.partner-card {
  @apply tw-cursor-pointer;
  border: 1px solid #dfdfe6;
  border-radius: 6px;
  @apply tw-p-4;

  & svg {
    @apply tw-fill-gray-900;
  }

  &:hover {
    @apply tw-bg-true-gray-100;
    @apply tw-cursor-pointer;

    & svg {
      @apply tw-fill-gray-900;
    }
  }
}

.recruiter-card {
  @apply tw-cursor-pointer;
  @apply tw-bg-true-gray-100;
  @apply tw-p-4;
  border-radius: 6px;

  &:hover {
    @apply tw-bg-true-gray-300;
  }

  & svg {
    @apply tw-fill-gray-700;

    &:hover {
      @apply tw-cursor-pointer;
      @apply tw-fill-gray-600;
    }
  }

  .click-to-expand {
    @apply tw-select-none;
  }

  .plus {
    @apply tw-mt-3;
    @apply tw-mr-1;
    transition: transform 0.35s;
    transform: rotate(45deg);
  }

  .read-more {
    @apply tw-inline-block;
    @apply tw-text-base;
    @apply tw-mt-4;
  }

  .animate-plus {
    transform: rotate(90deg);
  }

  .recruiter-animation-enter-active,
  .recruiter-animation-leave-active {
    transition: max-height 0.2s linear;
    @apply tw-overflow-hidden;
  }
  .recruiter-animation-enter-from,
  .recruiter-animation-leave-to {
    max-height: 0 !important;
  }
}
</style>
